import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Address } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import { paths } from 'frameworks_drivers/routes';
import { TrackerUtil } from 'tracking/TrackerUtil';
import HouseAddressForm from './components/HouseAddressForm';
import { ReduxAddressRepository } from '../../../../interface_adapters/services/ReduxAddressRepository';
import {
  CheckInvoiceAddressUseCase,
  GetHouseAddressUseCase,
  SaveHouseAddressUseCase
} from '../../../../application/use_cases/AddressUseCases';
import { useFastLane } from '../../../../interface_adapters/hooks/useFastLane';

const HouseAddressPage: React.FC = () => {
  const navigate = useNavigate();
  const prevPath = paths.InvoiceAddress;

  const { t } = useTranslation('translation', {
    keyPrefix: 'page.houseAddress'
  });

  const { setFastLane } = useFastLane();

  // Repository und UseCases mit useMemo cachen
  const addressRepository = useMemo(() => new ReduxAddressRepository(), []);
  const getHouseAddressUseCase = useMemo(
    () => new GetHouseAddressUseCase(addressRepository),
    [addressRepository]
  );
  const saveHouseAddressUseCase = useMemo(
    () => new SaveHouseAddressUseCase(addressRepository),
    [addressRepository]
  );
  const checkInvoiceAddressUseCase = useMemo(
    () => new CheckInvoiceAddressUseCase(addressRepository),
    [addressRepository]
  );

  // Standard-Formularsteuerung ohne AssistedAddressSearchContext
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      zip: '',
      city: '',
      street: '',
      houseNr: '',
      fastLane: ''
    }
  });

  // Werte aus Store wiederherstellen
  const restoreFormFields = useCallback(
    (storedAddress: Address) => {
      setValue('zip', storedAddress.zip);
      setValue('city', storedAddress.city);
      setValue('street', storedAddress.street);
      setValue('houseNr', storedAddress.houseNumber);
    },
    [setValue]
  );

  useEffect(() => {
    if (!DISABLE_FLOW_DEEP_LINKING_PROTECTION && !checkInvoiceAddressUseCase.execute()) {
      navigate(prevPath, { replace: true });
    }
    const storedAddress = getHouseAddressUseCase.execute();
    restoreFormFields(storedAddress);
  }, [prevPath, navigate, restoreFormFields, checkInvoiceAddressUseCase, getHouseAddressUseCase]);

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  const onSubmit = handleSubmit(({ street, houseNr, zip, city, fastLane }, e) => {
    e?.preventDefault();
    TrackerUtil.trackNextBtnClick();

    saveHouseAddressUseCase.execute({
      street,
      houseNumber: houseNr,
      zip,
      city
    });

    if (fastLane && fastLane !== '') setFastLane(fastLane);

    navigate(paths.BillingPeriod);
  });

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer>
        <WizardSteps currentStep={3} totalSteps={4} />
      </WizardStepsContainer>

      <TitleWithPrevPageLink onPrevClick={handlePrevBtnClicked} title={t('title')} />

      <HouseAddressForm
        formControl={control}
        errors={errors}
        setValue={setValue}
        onSubmit={onSubmit}
      />
    </Container>
  );
};

export default HouseAddressPage;
